import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, downloadBase64 } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Gift, Printer, Send, ExternalLink, Loader2, Truck } from "lucide-react";
import { toast } from "sonner";
import { useSortable } from "@/lib/sortable";
import { usePersistedState } from "@/lib/persist";
import { VerzendingHealthBanner } from "@/components/VerzendingHealthBanner";

const TABS = [
  { id: "openstaand", label: "Openstaand" },
  { id: "geprint", label: "Geprint" },
  { id: "verzonden", label: "Verzonden" },
];

export default function Samples() {
  const [items, setItems] = useState([]);
  const [q, setQ] = usePersistedState("samples:q", "");
  const [tab, setTab] = usePersistedState("samples:tab", "openstaand");
  const [selected, setSelected] = useState(new Set());
  const [printing, setPrinting] = useState(false);
  const [verzenden, setVerzenden] = useState(false);
  const [labels, setLabels] = useState({}); // id -> lokaal bewerkte notitie_label
  const [briefTemplates, setBriefTemplates] = useState([]); // samplebrief-designs uit de bibliotheek
  const [briefTemplate, setBriefTemplate] = useState("_gekoppeld");

  const labelVal = (s) => (labels[s.id] !== undefined ? labels[s.id] : (s.notitie_label || ""));
  const saveLabel = async (s) => {
    const val = labelVal(s);
    if (val === (s.notitie_label || "")) return;
    try {
      await api.patch(`/verkoopkansen/${s.id}`, { notitie_label: val });
      setItems((prev) => prev.map((x) => (x.id === s.id ? { ...x, notitie_label: val } : x)));
      toast.success("Notitie label opgeslagen");
    } catch { toast.error("Opslaan mislukt"); }
  };

  const load = async () => {
    const { data } = await api.get("/samples");
    setItems(data);
  };
  useEffect(() => {
    load();
    api.get("/templates").then(({ data }) => {
      setBriefTemplates((data.designs || []).filter((d) => d.type === "samplebrief"));
    }).catch(() => {});
  }, []);

  const byTab = items.filter((s) => s.status === tab);
  const filtered = byTab.filter((s) => {
    if (!q) return true;
    const t = q.toLowerCase();
    return (s.organisatie || "").toLowerCase().includes(t)
      || (s.naam || "").toLowerCase().includes(t)
      || (s.plaats || "").toLowerCase().includes(t);
  });
  // volgorde = aanvraagdatum (fase-overgang naar 'Sample opsturen'), oplopend
  const { sortedRows, SortHeader } = useSortable(filtered, "sample_aangevraagd_op", "asc");

  const counts = useMemo(() => ({
    openstaand: items.filter((s) => s.status === "openstaand").length,
    geprint: items.filter((s) => s.status === "geprint").length,
    verzonden: items.filter((s) => s.status === "verzonden").length,
  }), [items]);

  const switchTab = (t) => { setTab(t); setSelected(new Set()); };
  const toggle = (id) => setSelected((prev) => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const allSelected = sortedRows.length > 0 && sortedRows.every((s) => selected.has(s.id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(sortedRows.map((s) => s.id)));

  // ids in de exacte weergavevolgorde (zodat brieven en labels overeenkomen)
  const geselecteerdeIdsInVolgorde = () => sortedRows.filter((s) => selected.has(s.id)).map((s) => s.id);

  const printSamples = async () => {
    const ids = geselecteerdeIdsInVolgorde();
    if (!ids.length) { toast.error("Selecteer minstens één sample"); return; }
    setPrinting(true);
    try {
      const { data } = await api.post("/samples/print", {
        ids, template_id: briefTemplate === "_gekoppeld" ? null : briefTemplate,
      });
      const stamp = new Date().toISOString().slice(0, 10);
      if (data.samplebrieven_pdf_base64) {
        downloadBase64(data.samplebrieven_pdf_base64, data.samplebrieven_bestandsnaam || `samplebrieven-${stamp}.pdf`);
      }
      if (data.labels_pdf_base64) {
        // korte vertraging zodat beide downloads los starten
        setTimeout(() => downloadBase64(data.labels_pdf_base64, data.labels_bestandsnaam || `verzendlabels-${stamp}.pdf`), 600);
      }
      toast.success(`${data.geprint} sample(s) geprint — brieven en labels gedownload in dezelfde volgorde`);
      if (data.bericht) toast.info(data.bericht, { duration: 9000 });
      if (data.fouten?.length) toast.error(`${data.fouten.length} probleem/problemen: ${data.fouten.join(" | ")}`, { duration: 10000 });
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Printen mislukt");
    } finally {
      setPrinting(false);
    }
  };

  const markeerVerzonden = async () => {
    const ids = geselecteerdeIdsInVolgorde();
    if (!ids.length) { toast.error("Selecteer minstens één sample"); return; }
    setVerzenden(true);
    try {
      const { data } = await api.post("/samples/verzonden", { ids });
      toast.success(`${data.verzonden} sample(s) verzonden — verkoopkans → 'Verzonden samples'`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Markeren mislukt");
    } finally {
      setVerzenden(false);
    }
  };

  const showSelect = tab === "openstaand" || tab === "geprint";

  return (
    <div className="space-y-6" data-testid="samples-page">
      <div>
        <div className="text-sm text-muted-foreground flex items-center gap-1.5"><Gift className="h-3.5 w-3.5 text-accent" /> Logistiek</div>
        <h1 className="font-display text-4xl font-medium tracking-tight mt-1">Samples</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Print samplebrieven én verzendlabels in dezelfde volgorde en verwerk ze eenvoudig van openstaand naar verzonden.
        </p>
      </div>

      <VerzendingHealthBanner />

      <div className="flex items-center gap-1 border-b border-border">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => switchTab(t.id)} data-testid={`samples-tab-${t.id}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            {t.label} <span className="ml-1 text-xs opacity-70">({counts[t.id]})</span>
          </button>
        ))}
      </div>

      <div className="bg-card border border-border rounded-xl p-3 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder="Zoek op organisatie, plaats of kans…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="samples-search" />
        </div>
        {tab === "openstaand" && (
          <>
            {briefTemplates.length > 0 && (
              <Select value={briefTemplate} onValueChange={setBriefTemplate}>
                <SelectTrigger className="h-9 w-64" data-testid="samples-template-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_gekoppeld">Samplebrief: gekoppeld template</SelectItem>
                  {briefTemplates.map((d) => <SelectItem key={d.id} value={d.id}>{d.naam}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <Button onClick={printSamples} disabled={printing || selected.size === 0} data-testid="samples-print-btn">
              {printing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Printer className="h-4 w-4 mr-1" />}
              {printing ? "Bezig…" : `Print brieven + labels${selected.size ? ` · ${selected.size}` : ""}`}
            </Button>
          </>
        )}
        {tab === "geprint" && (
          <Button onClick={markeerVerzonden} disabled={verzenden || selected.size === 0} variant="secondary" data-testid="samples-verzonden-btn">
            {verzenden ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
            {verzenden ? "Bezig…" : `Markeer als verzonden${selected.size ? ` · ${selected.size}` : ""}`}
          </Button>
        )}
      </div>

      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 text-xs uppercase text-muted-foreground">
            <tr>
              {showSelect && (
                <th className="p-3 w-10">
                  <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="samples-select-all" />
                </th>
              )}
              <th className="p-3 text-left w-12">#</th>
              <SortHeader k="organisatie" accessor={(s) => s.organisatie || ""}>Organisatie</SortHeader>
              <SortHeader k="naam" accessor={(s) => s.naam || ""}>Verkoopkans</SortHeader>
              <th className="p-3 text-left">Plaats</th>
              <th className="p-3 text-left">Notitie label</th>
              <th className="p-3 text-right">Regels</th>
              <SortHeader k="sample_aangevraagd_op" accessor={(s) => s.sample_aangevraagd_op || ""}>Aangevraagd</SortHeader>
              <th className="p-3 text-right">Actie</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sortedRows.map((s, i) => (
              <tr key={s.id} data-testid={`samples-rij-${s.id}`}>
                {showSelect && (
                  <td className="p-3">
                    <input type="checkbox" checked={selected.has(s.id)} onChange={() => toggle(s.id)} data-testid={`samples-check-${s.id}`} />
                  </td>
                )}
                <td className="p-3 text-muted-foreground tabular-nums">{i + 1}</td>
                <td className="p-3 font-medium">
                  <Link to={`/organisaties/${s.relatie_id}`} className="text-primary hover:underline">{s.organisatie || "—"}</Link>
                </td>
                <td className="p-3">
                  <Link to={`/verkoopkansen/${s.id}`} className="hover:underline inline-flex items-center">
                    {s.naam || "—"} <ExternalLink className="h-3 w-3 ml-1 text-muted-foreground" />
                  </Link>
                </td>
                <td className="p-3 text-muted-foreground">{s.plaats || "—"}</td>
                <td className="p-3">
                  <Input value={labelVal(s)}
                    onChange={(e) => setLabels((m) => ({ ...m, [s.id]: e.target.value }))}
                    onBlur={() => saveLabel(s)}
                    placeholder="MyParcel-referentie…"
                    className="h-8 text-sm min-w-[150px]" data-testid={`samples-notitie-label-${s.id}`} />
                </td>
                <td className="p-3 text-right tabular-nums">{s.aantal_regels}</td>
                <td className="p-3 text-muted-foreground">{fmtDate(s.sample_aangevraagd_op)}</td>
                <td className="p-3 text-right">
                  {s.status === "geprint" && <Badge variant="outline" className="text-[10px]">Geprint</Badge>}
                  {s.status === "verzonden" && (
                    s.tracktrace_url
                      ? <a href={s.tracktrace_url} target="_blank" rel="noreferrer" className="inline-flex items-center text-primary hover:underline text-xs"><Truck className="h-3 w-3 mr-1" /> {s.myparcel_barcode || "Track & Trace"}</a>
                      : <Badge className="bg-emerald-500 text-white hover:bg-emerald-500 text-[10px]">Verzonden</Badge>
                  )}
                </td>
              </tr>
            ))}
            {sortedRows.length === 0 && (
              <tr><td colSpan={showSelect ? 9 : 8} className="p-10 text-center text-muted-foreground">
                {tab === "openstaand" ? "Geen openstaande samples. Zet een verkoopkans via de bel-log op 'Sample opsturen'."
                  : tab === "geprint" ? "Nog niets geprint."
                  : "Nog geen verzonden samples."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-muted-foreground">
        Tip: selecteer openstaande samples en klik <strong>Print brieven + labels</strong>. De samplebrieven en de MyParcel-verzendlabels worden in exact dezelfde volgorde gedownload, zodat je ze direct bij elkaar kunt houden. Daarna verplaatsen ze naar <strong>Geprint</strong>; na fysiek verzenden markeer je ze als <strong>Verzonden</strong>.
      </p>
    </div>
  );
}
